/**
 * "Open device" (ENG-2470 Phase 2): a remote desktop on our network, on the
 * same network path as the site's connector, with Chromium open on the
 * device's web page and VLC for "does this RTSP stream play?". The user
 * signs in to the device themselves; only pixels reach this browser.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import {
  actionRefusalText,
  defaultUseHttps,
  sendDeviceSessionAction,
  setDeviceSessionBrowserIdentity,
} from "./deviceSessionApi";
import {
  DeviceSessionAction,
  DeviceSessionBrowserIdentity,
  DeviceSessionCompatHint,
  DeviceSessionPortSource,
  DeviceSessionTarget,
} from "./deviceSessionTypes";
import { DeviceSessionScreen } from "./DeviceSessionScreen";
import { RfbClient } from "./rfbClient";
import { outcomeMessage, useDeviceSession } from "./useDeviceSession";
import { SOURCE_LABEL } from "./deviceSessionTargets";
import { UseHttpsCheckbox } from "./UseHttpsCheckbox";

export const OPEN_DEVICE_LABEL = "Open device";
export const DEVICE_SESSION_EXPLAINER =
  "A secure browser on our network, on the same path as your site's connector, opens the device's web page. Sign in to the device as you normally would. Closing this window ends the session.";
export const STARTING_HINT =
  "A new session can take up to 90 seconds to start.";

/** Sources with an RTSP stream to open in VLC (design §6). */
const HAS_STREAM = new Set(["camera", "server"]);

/** "1:05" */
export const formatDuration = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/** "12:34 left", or null without a deadline. */
export const timeLeftText = (
  expiresAt: string | null,
  now: number
): string | null => {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return null;
  return `${formatDuration(at - now)} left`;
};

const useNow = (running: boolean) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return now;
};

const ACTION_PENDING: Record<DeviceSessionAction, string> = {
  open_web: "Opening the web page…",
  vlc: "Opening the stream in VLC… VLC asks for the camera's login.",
  ffprobe: "Reading the stream…",
};

/** Where each allowed port came from, in plain words. */
export const PORT_SOURCE_LABEL: Record<string, string> = {
  camera_http: "Camera web port",
  camera_server_port: "Camera server port",
  site_http_port: "Site HTTP port",
  server_port: "Server port (used for auto-add)",
  stream_port: "Stream port",
  milestone_http: "Milestone web port",
  milestone_ssl: "Milestone secure port",
  alert_port: "Alert port",
  camera_rtsp: "Camera stream port",
  default_80: "Standard web port 80",
  default_443: "Standard web port 443",
  default_554: "Standard stream port 554",
};
export const FALLBACK_PORT_NOTE =
  "Fallback — on a port-forwarded public address this may reach the router, not the device.";

const isFallbackPort = (entry: DeviceSessionPortSource) =>
  entry.source === "default_80" ||
  entry.source === "default_443" ||
  entry.source === "default_554";

const portLabel = (entry: DeviceSessionPortSource): string | null =>
  (entry.source && PORT_SOURCE_LABEL[entry.source]) || null;

const SSL_PORTS = new Set([443, 8443, 7443]);

/** "https://10.0.0.5:443" */
export const webPortUrl = (
  address: string,
  entry: DeviceSessionPortSource
): string => {
  const scheme =
    entry.scheme === "http" || entry.scheme === "https"
      ? entry.scheme
      : SSL_PORTS.has(entry.port)
      ? "https"
      : "http";
  return `${scheme}://${address}:${entry.port}`;
};

/** "Opens https://10.0.0.5:443 — Camera web port", plus the fallback note. */
const StartPortLine = ({
  address,
  entry,
}: {
  address: string;
  entry: DeviceSessionPortSource;
}) => {
  const label = portLabel(entry);
  return (
    <span
      className="block font-mulish font-normal text-[13px] text-[#4c5257]"
      data-testid="device-session-start-port"
    >
      Opens {webPortUrl(address, entry)}
      {label ? ` — ${label}` : ""}
      {isFallbackPort(entry) && (
        <span className="block text-[12px] text-[#6b7280]">
          {FALLBACK_PORT_NOTE}
        </span>
      )}
    </span>
  );
};

/**
 * A toolbar button for one of the session's ports. With several allowed
 * ports it opens a menu listing each with where it came from.
 */
const PortActionButton = ({
  children,
  ports,
  describe,
  onPick,
  testId,
}: {
  children: React.ReactNode;
  ports: DeviceSessionPortSource[];
  describe: (entry: DeviceSessionPortSource) => string;
  onPick: (port: number | undefined) => void;
  testId: string;
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  if (ports.length <= 1) {
    return (
      <TsButton
        variant="outline"
        size="sm"
        onClick={() => onPick(ports[0]?.port)}
        data-testid={testId}
      >
        {children}
      </TsButton>
    );
  }
  return (
    <>
      <TsButton
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        data-testid={testId}
      >
        {children} ▾
      </TsButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        data-testid={`${testId}-menu`}
      >
        {ports.map((entry) => {
          const label = portLabel(entry);
          return (
            <MenuItem
              key={`${entry.port}-${entry.source ?? ""}`}
              onClick={() => {
                setAnchor(null);
                onPick(entry.port);
              }}
              data-testid={`${testId}-port-${entry.port}`}
            >
              <ListItemText
                primary={describe(entry)}
                secondary={
                  [label, isFallbackPort(entry) ? FALLBACK_PORT_NOTE : null]
                    .filter(Boolean)
                    .join(" · ") || null
                }
                primaryTypographyProps={{
                  fontFamily: "Mulish",
                  fontSize: 13,
                }}
                secondaryTypographyProps={{
                  fontFamily: "Mulish",
                  fontSize: 12,
                  sx: { whiteSpace: "normal", maxWidth: 360 },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/** "Port 554 — Stream port" next to the single stream button. */
const PortCaption = ({ entry }: { entry: DeviceSessionPortSource }) => {
  const label = portLabel(entry);
  return (
    <span
      className="font-mulish text-[12px] text-[#4c5257]"
      data-testid="device-session-stream-port"
    >
      Port {entry.port}
      {label ? ` — ${label}` : ""}
      {isFallbackPort(entry) ? ` (${FALLBACK_PORT_NOTE})` : ""}
    </span>
  );
};

/** The stream player was opened already signed in (admin `prefilled`). */
export const STREAM_PREFILLED_NOTE = "Signed in with saved camera credentials";

const streamPrefilled = (data: unknown): boolean =>
  !!data &&
  typeof data === "object" &&
  (data as { prefilled?: unknown }).prefilled === true;

/** Customer-facing names for the remote browser's identity (ENG-2507). */
export const BROWSER_LABEL: Record<DeviceSessionBrowserIdentity, string> = {
  chromium: "Chrome",
  edge: "Edge",
};
export const BROWSER_SWITCHING =
  "Switching the browser… The page reloads in a few seconds.";
export const EDGE_SUGGESTION = "This page may require Microsoft Edge";
export const PLUGIN_NOTICE =
  "This device's page needs a Windows plugin, which the remote browser can't run.";

/** "Browser: Chrome ▾" — relaunches the remote browser as Chrome or Edge. */
const BrowserPicker = ({
  value,
  pending,
  onChange,
}: {
  value: DeviceSessionBrowserIdentity;
  pending: boolean;
  onChange: (identity: DeviceSessionBrowserIdentity) => void;
}) => (
  <label className="flex items-center gap-1 font-mulish text-[13px] text-[#4c5257]">
    Browser:
    <select
      value={value}
      disabled={pending}
      aria-busy={pending}
      onChange={(event) => {
        const next = event.target.value as DeviceSessionBrowserIdentity;
        onChange(next);
      }}
      className="h-[30px] rounded border border-[#d1d5db] bg-white px-1 font-mulish text-[13px] text-actuate-blue disabled:opacity-60"
      data-testid="device-session-browser"
    >
      {(["chromium", "edge"] as DeviceSessionBrowserIdentity[]).map((id) => (
        <option key={id} value={id}>
          {BROWSER_LABEL[id]}
        </option>
      ))}
    </select>
    {pending && <CircularProgress size={14} sx={{ color: "#001943" }} />}
  </label>
);

const hintKey = (hint: DeviceSessionCompatHint) =>
  `${hint.kind}|${hint.at ?? ""}|${hint.host ?? ""}`;

/** What the session noticed about the device page, with the way out. */
const CompatHintBanner = ({
  hint,
  identity,
  pending,
  onSwitchToEdge,
  onDismiss,
}: {
  hint: DeviceSessionCompatHint;
  identity: DeviceSessionBrowserIdentity;
  pending: boolean;
  onSwitchToEdge: () => void;
  onDismiss: () => void;
}) => {
  const refused = hint.kind === "browser_refused";
  if (refused && identity === "edge") return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 mb-2 rounded-md border border-actuate-orange bg-[#fff4e5] px-3 py-2"
      role="status"
      data-testid={`device-session-hint-${hint.kind}`}
    >
      <p className="m-0 flex-1 font-mulish text-[13px] text-actuate-blue">
        {refused ? `${EDGE_SUGGESTION} —` : PLUGIN_NOTICE}
        {refused && (
          <TsButton
            variant="quiet"
            size="sm"
            onClick={onSwitchToEdge}
            disabled={pending}
            sx={{ ml: 0.5 }}
            data-testid="device-session-switch-edge"
          >
            Switch to Edge
          </TsButton>
        )}
      </p>
      <IconButton
        aria-label="Dismiss"
        size="small"
        onClick={onDismiss}
        data-testid="device-session-hint-dismiss"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

/** Staff only (policy=staff): send text to the remote clipboard. */
const PasteIn = ({ client }: { client: RfbClient | null }) => {
  const [value, setValue] = React.useState("");
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        if (!client || !value) return;
        client.clipboardPasteFrom(value);
        setValue("");
        client.focus();
      }}
    >
      <input
        type="password"
        aria-label="Text to paste into the session"
        placeholder="Paste in…"
        autoComplete="off"
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
        }}
        className="h-[30px] w-[140px] rounded border border-[#d1d5db] px-2 font-mulish text-[13px]"
        data-testid="device-session-paste-input"
      />
      <TsButton
        variant="quiet"
        size="sm"
        type="submit"
        disabled={!client || !value}
        data-testid="device-session-paste"
      >
        Send to clipboard
      </TsButton>
    </form>
  );
};

export const DeviceSessionDialog = ({
  open,
  onClose,
  target,
}: {
  open: boolean;
  onClose: () => void;
  target: DeviceSessionTarget | null;
}) => {
  const { state, start, end, onConnected, onDisconnected, setBrowserIdentity } =
    useDeviceSession(target, open);
  const {
    phase,
    sessionId,
    credentials,
    expiresAt,
    policy,
    browserIdentity,
    compatHint,
    targetIp,
    webPorts,
    rtspPorts,
    startPort,
    scheme: startScheme,
  } = state;
  const address = targetIp || target?.address || null;
  const startEntry =
    startPort != null
      ? webPorts.find((entry) => entry.port === startPort) ?? {
          port: startPort,
        }
      : null;
  const live = phase.kind === "connecting" || phase.kind === "connected";
  const now = useNow(open && (phase.kind === "starting" || live));
  const [client, setClient] = React.useState<RfbClient | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [switching, setSwitching] = React.useState(false);
  const [dismissedHint, setDismissedHint] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!live) setNotice(null);
  }, [live]);

  const switchBrowser = async (identity: DeviceSessionBrowserIdentity) => {
    if (!sessionId || switching || identity === browserIdentity) return;
    setSwitching(true);
    setNotice(BROWSER_SWITCHING);
    const outcome = await setDeviceSessionBrowserIdentity(sessionId, identity);
    setSwitching(false);
    if (outcome.kind === "ok") {
      setBrowserIdentity(identity);
      setNotice(null);
    } else {
      setNotice(actionRefusalText(outcome) ?? outcomeMessage(outcome));
    }
    client?.focus();
  };

  const visibleHint =
    compatHint && hintKey(compatHint) !== dismissedHint ? compatHint : null;

  // The web port last opened (initially the start port) and its scheme,
  // for the toolbar's "Use HTTPS".
  const [lastWeb, setLastWeb] = React.useState<{
    port: number;
    scheme: "http" | "https" | null;
  } | null>(null);
  React.useEffect(() => setLastWeb(null), [sessionId]);
  const currentPort = lastWeb?.port ?? startPort;
  const currentEntry = webPorts.find((entry) => entry.port === currentPort);
  const currentScheme =
    lastWeb?.scheme ??
    (lastWeb ? null : startScheme) ??
    (currentEntry?.scheme === "http" || currentEntry?.scheme === "https"
      ? currentEntry.scheme
      : null);
  const useHttps =
    currentScheme != null
      ? currentScheme === "https"
      : defaultUseHttps(currentPort, currentEntry?.scheme);

  const runAction = async (
    action: DeviceSessionAction,
    port?: number,
    scheme?: "http" | "https"
  ) => {
    if (!sessionId) return;
    setNotice(ACTION_PENDING[action]);
    const outcome = scheme
      ? await sendDeviceSessionAction(sessionId, action, port, scheme)
      : port != null
      ? await sendDeviceSessionAction(sessionId, action, port)
      : await sendDeviceSessionAction(sessionId, action);
    if (outcome.kind !== "ok")
      setNotice(actionRefusalText(outcome) ?? outcomeMessage(outcome));
    else if (action === "vlc" && streamPrefilled(outcome.data))
      setNotice(STREAM_PREFILLED_NOTE);
    client?.focus();
  };

  const timeLeft = live ? timeLeftText(expiresAt, now) : null;

  return (
    <Dialog
      open={open}
      // Only the close button closes: Escape belongs to the device, and a
      // stray backdrop click shouldn't end the session.
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xl"
      data-testid="device-session-dialog"
    >
      <DialogTitle
        sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943", pr: 6 }}
      >
        {OPEN_DEVICE_LABEL}
        {target ? `: ${target.label}` : ""}
        {target ? (
          <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
            {SOURCE_LABEL[target.source]}
          </span>
        ) : null}
        {address && startEntry ? (
          <StartPortLine address={address} entry={startEntry} />
        ) : null}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ position: "relative" }}>
        <p
          className="m-0 mb-3 font-mulish text-[13px] text-[#4c5257]"
          data-testid="device-session-explainer"
        >
          {DEVICE_SESSION_EXPLAINER}
        </p>

        {phase.kind === "starting" && (
          <div
            className="flex flex-col items-center justify-center gap-3 h-[60vh] rounded-md bg-[#f3f4f6]"
            role="status"
            data-testid="device-session-starting"
          >
            <CircularProgress size={36} sx={{ color: "#001943" }} />
            <p className="m-0 font-mulish font-bold text-[16px] text-actuate-blue">
              Starting… {formatDuration(now - phase.startedAt)}
            </p>
            <p className="m-0 font-mulish text-[13px] text-[#4c5257]">
              {STARTING_HINT}
            </p>
          </div>
        )}

        {live && (
          <>
            <div
              className="flex flex-wrap items-center gap-2 mb-2"
              data-testid="device-session-toolbar"
            >
              <PortActionButton
                ports={webPorts}
                describe={(entry) =>
                  address ? webPortUrl(address, entry) : `Port ${entry.port}`
                }
                onPick={(port) => {
                  if (port != null) {
                    const entry = webPorts.find((e) => e.port === port);
                    setLastWeb({
                      port,
                      scheme:
                        entry?.scheme === "http" || entry?.scheme === "https"
                          ? entry.scheme
                          : null,
                    });
                  }
                  runAction("open_web", port);
                }}
                testId="device-session-web"
              >
                Web page
              </PortActionButton>
              {currentPort != null && (
                <UseHttpsCheckbox
                  checked={useHttps}
                  onChange={(next) => {
                    const nextScheme = next ? "https" : "http";
                    setLastWeb({ port: currentPort, scheme: nextScheme });
                    runAction("open_web", currentPort, nextScheme);
                  }}
                  testId="device-session-https"
                />
              )}
              {target && HAS_STREAM.has(target.source) && (
                <>
                  <PortActionButton
                    ports={rtspPorts}
                    describe={(entry) => `Port ${entry.port}`}
                    onPick={(port) => runAction("vlc", port)}
                    testId="device-session-vlc"
                  >
                    Open stream in VLC
                  </PortActionButton>
                  {rtspPorts.length === 1 && (
                    <PortCaption entry={rtspPorts[0]} />
                  )}
                </>
              )}
              <BrowserPicker
                value={browserIdentity}
                pending={switching}
                onChange={switchBrowser}
              />
              {policy === "staff" && <PasteIn client={client} />}
              <span className="flex-1" />
              {timeLeft && (
                <span
                  className="font-mulish text-[13px] text-[#4c5257] tabular-nums"
                  data-testid="device-session-time-left"
                >
                  {timeLeft}
                </span>
              )}
              <TsButton
                variant="primary"
                size="sm"
                onClick={() => end()}
                data-testid="device-session-end"
              >
                End session
              </TsButton>
            </div>
            {visibleHint && (
              <CompatHintBanner
                hint={visibleHint}
                identity={browserIdentity}
                pending={switching}
                onSwitchToEdge={() => switchBrowser("edge")}
                onDismiss={() => setDismissedHint(hintKey(visibleHint))}
              />
            )}
            {notice && (
              <p
                className="m-0 mb-2 font-mulish text-[13px] text-[#4c5257]"
                role="status"
                data-testid="device-session-notice"
              >
                {notice}
              </p>
            )}
            <div className="relative w-full h-[70vh]">
              {credentials && (
                <DeviceSessionScreen
                  key={credentials.token}
                  credentials={credentials}
                  onConnected={onConnected}
                  onDisconnected={onDisconnected}
                  onClient={setClient}
                />
              )}
              {phase.kind === "connecting" && (
                <div
                  className="absolute inset-0 flex items-center justify-center gap-2 bg-[#0b1220]/70 font-mulish text-[14px] text-white"
                  role="status"
                  data-testid="device-session-connecting"
                >
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  {phase.reconnect ? "Reconnecting…" : "Connecting…"}
                </div>
              )}
            </div>
          </>
        )}

        {(phase.kind === "refused" ||
          phase.kind === "ended" ||
          phase.kind === "failed") && (
          <div
            className={`rounded-md border px-4 py-3 ${
              phase.kind === "ended"
                ? "border-[#d1d5db] bg-[#f9fafb]"
                : "border-actuate-orange bg-[#fff4e5]"
            }`}
            role={phase.kind === "ended" ? "status" : "alert"}
            data-testid={`device-session-${phase.kind}`}
          >
            <p className="m-0 font-mulish text-[14px] text-actuate-blue">
              {phase.message}
            </p>
            <div className="flex gap-2 mt-3">
              <TsButton
                variant="outline"
                size="sm"
                onClick={() => start()}
                data-testid="device-session-retry"
              >
                {phase.kind === "ended" ? "Open again" : "Try again"}
              </TsButton>
              <TsButton variant="quiet" size="sm" onClick={onClose}>
                Close
              </TsButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
